import { ref, listAll, getDownloadURL, getMetadata } from 'firebase/storage';
import { storage } from '../config/firebase';
import { getCurrentUserStoragePath } from '../utils/domainUtils';

export interface StorageItem {
  name: string;
  path: string;
  type: 'file' | 'folder';
  size?: number;
  downloadURL?: string;
  lastModified?: Date;
  contentType?: string;
}

export class StorageService {
  private static getUserBasePath(): string {
    const domainPath = getCurrentUserStoragePath();
    return `flashchecks/${domainPath}`;
  }

  static async listFiles(relativePath: string = ''): Promise<StorageItem[]> {
    try {
      const userBasePath = this.getUserBasePath();
      const fullPath = relativePath ? `${userBasePath}/${relativePath}` : userBasePath;
      
      const storageRef = ref(storage, fullPath);
      const result = await listAll(storageRef);
      
      const items: StorageItem[] = [];
      
      // Add folders
      for (const folderRef of result.prefixes) {
        items.push({
          name: folderRef.name,
          path: folderRef.fullPath.replace(`${userBasePath}/`, ''), // Store relative path
          type: 'folder'
        });
      }
      
      // Add files
      for (const fileRef of result.items) {
        try {
          const metadata = await getMetadata(fileRef);
          const downloadURL = await getDownloadURL(fileRef);
          
          items.push({
            name: fileRef.name,
            path: fileRef.fullPath.replace(`${userBasePath}/`, ''), // Store relative path
            type: 'file',
            size: metadata.size,
            downloadURL,
            lastModified: new Date(metadata.timeCreated),
            contentType: metadata.contentType
          });
        } catch (error) {
          // Add file without metadata if there's an error
          items.push({
            name: fileRef.name,
            path: fileRef.fullPath.replace(`${userBasePath}/`, ''), // Store relative path
            type: 'file'
          });
        }
      }
      
      return items.sort((a, b) => {
        // Sort folders first, then files
        if (a.type !== b.type) {
          return a.type === 'folder' ? -1 : 1;
        }
        return a.name.localeCompare(b.name);
      });
    } catch (error: any) {
      
      if (error instanceof Error && error.message.includes('authenticated')) {
        throw error;
      }
      
      // Check for specific Firebase errors
      if (error.code === 'storage/unauthorized') {
        throw new Error('Access denied. Please check your email domain permissions.');
      } else if (error.code === 'storage/object-not-found') {
        // This is actually OK - it just means the folder doesn't exist yet
        return [];
      } else if (error.code === 'storage/unknown') {
        throw new Error('Firebase Storage error. Please try again or contact support.');
      }
      
      throw new Error(`Failed to load files: ${error.message}`);
    }
  }
  
  static async getFileURL(relativePath: string): Promise<string> {
    try {
      const userBasePath = this.getUserBasePath();
      const fullPath = relativePath ? `${userBasePath}/${relativePath}` : userBasePath;
      
      const fileRef = ref(storage, fullPath);
      const url = await getDownloadURL(fileRef);
      
      return url;
    } catch (error) {
      throw new Error('Failed to get file URL');
    }
  }
}
